import { BevelCut, BottomBevelCut, CalculatedLine, Component, Member } from '@paragon/contract-library';
import { calculateLine } from '@paragon/relative-geometry';

import { setMemberBevelCut, setMemberBottomBevelCut } from '../../store/actions/editMemberActions';
import { handleReferenceLineClickDuringMemberCuttingMode } from '../../store/actions/memberCuttingModeActions';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import {
  describeMemberGeometry,
  findMemberByName,
  openTruss,
  optionalNumber,
  requireString,
  requireStringArray,
  summarizeTruss,
  waitForAppToSettle,
} from './helpers';
import { MEMBER_SCHEMA } from './trussTools';
import { defineTool, ToolError } from './types';

/**
 * Cutting members as the app's Cut Member mode does: pick a member, then the face of another member to cut it against.
 * A member's faces are reference lines: the line it's built on and the lines along its sides.
 */

const TRUSS_SCHEMA = { type: 'string', description: 'The truss’s name.' };

type Point = { readonly x: number; readonly y: number };

/** The member a reference line belongs to, or the line's own name, such as "Overhang Left" or "Splice 1". */
export function nameReferenceLine(component: Component, line: CalculatedLine) {
  const owner = component.members.find(
    (member) =>
      member.relativeGeometry != null &&
      (line.lineType === 'Member'
        ? line.memberGeometryGuid === member.relativeGeometry.guid
        : member.relativeGeometry.lineGuid === line.guid),
  );
  if (owner != null) return owner.name;
  return 'name' in line ? line.name : line.lineType;
}

/**
 * What each end of a member is cut against, by the names of the members whose faces cut it. The base end is where its
 * centerline starts (`from`).
 */
export function describeCuts(member: Member, component: Component) {
  const cuts = member.relativeGeometry;
  if (cuts == null || cuts.geometryType !== 'Member') return null;
  const lines = new Map((component.referenceLines ?? []).map((line) => [line.guid, line]));
  const names = (guids: readonly string[]) =>
    guids.map((guid) => {
      const line = lines.get(guid);
      return line == null ? 'a line no longer in the truss' : nameReferenceLine(component, line);
    });
  return { base: names(cuts.baseCutGuids), end: names(cuts.endCutGuids) };
}

/**
 * The lines of a member that others can be cut against, with where each lies: its sides, not its centerline. A member
 * is built on one line, along its center or one side, and has a reference line for each other side.
 */
function getFaces(component: Component, member: Member) {
  const geometry = member.relativeGeometry;
  if (geometry == null || geometry.geometryType !== 'Member') return [];
  return (component.referenceLines ?? []).flatMap((line) => {
    const isFace =
      line.lineType === 'Member'
        ? line.memberGeometryGuid === geometry.guid && line.lineLocation !== 'Center'
        : line.guid === geometry.lineGuid && geometry.lineLocation !== 'Center';
    if (!isFace) return [];
    const calculated = calculateLine(line, component);
    return calculated == null ? [] : [{ line, calculated }];
  });
}

function distanceToSegment(point: Point, from: Point, to: Point) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSquared = dx * dx + dy * dy;
  const t =
    lengthSquared === 0
      ? 0
      : Math.max(0, Math.min(1, ((point.x - from.x) * dx + (point.y - from.y) * dy) / lengthSquared));
  return Math.hypot(point.x - (from.x + t * dx), point.y - (from.y + t * dy));
}

/**
 * Chooses the face of `against` to cut `member` at, and the point on it where a designer would click. By default
 * that's the first face `member` reaches coming from its far end, so it stops at the near side of `against` even when
 * it runs past it now; given `at`, it's the face nearest that point.
 */
export function findCutFace(component: Component, member: Member, against: Member, at?: Point) {
  const memberLine = describeMemberGeometry(member, component).centerline;
  const againstLine = describeMemberGeometry(against, component).centerline;
  if (memberLine == null || againstLine == null)
    throw new ToolError(`The app can’t place ${member.name} or ${against.name} to cut one against the other.`);

  const distanceToAgainst = (point: Point) => distanceToSegment(point, againstLine.from, againstLine.to);
  const [near, far] =
    distanceToAgainst(memberLine.from) <= distanceToAgainst(memberLine.to)
      ? [memberLine.from, memberLine.to]
      : [memberLine.to, memberLine.from];
  const length = Math.hypot(near.x - far.x, near.y - far.y);
  const along = { x: (near.x - far.x) / length, y: (near.y - far.y) / length };

  const faces = getFaces(component, against).map(({ line, calculated }) => {
    const { basePoint, direction } = calculated;
    const toPoint = (point: Point) => {
      const t = (point.x - basePoint.x) * direction.x + (point.y - basePoint.y) * direction.y;
      return { x: basePoint.x + t * direction.x, y: basePoint.y + t * direction.y };
    };
    // Where the member's centerline, from its far end, crosses this face.
    const denominator = along.x * direction.y - along.y * direction.x;
    const reach =
      Math.abs(denominator) < 1e-9
        ? Infinity
        : ((basePoint.x - far.x) * direction.y - (basePoint.y - far.y) * direction.x) / denominator;
    const crossing = Number.isFinite(reach) ? { x: far.x + reach * along.x, y: far.y + reach * along.y } : null;
    const onLine = at != null ? toPoint(at) : (crossing ?? toPoint(near));
    const distance = at != null ? Math.hypot(at.x - onLine.x, at.y - onLine.y) : reach > 0 ? reach : Infinity;
    return { line, onLine, distance };
  });
  const face = faces.reduce<(typeof faces)[number] | null>(
    (nearest, candidate) => (nearest == null || candidate.distance < nearest.distance ? candidate : nearest),
    null,
  );
  if (face == null || !Number.isFinite(face.distance))
    throw new ToolError(`${member.name} doesn’t reach a face of ${against.name} it could be cut against.`);
  return face;
}

const BEVEL_SCHEMA = {
  type: 'object',
  description:
    'None removes the bevel. Single cuts one face at `angle` degrees (1 to 89) on the `side` given; Double cuts ' +
    'both faces, at frontAngle and backAngle. A bottom bevel is always Single.',
  properties: {
    type: { type: 'string', enum: ['None', 'Single', 'Double'] },
    side: { type: 'string', enum: ['Front', 'Back'] },
    angle: { type: 'number' },
    frontAngle: { type: 'number' },
    backAngle: { type: 'number' },
  },
  required: ['type'],
};

function requireAngle(input: Record<string, unknown>, key: string) {
  const angle = optionalNumber(input, key);
  if (angle == null || angle < 1 || angle > 89) throw new ToolError(`"${key}" must be an angle from 1 to 89 degrees.`);
  return angle;
}

function readBevelCut(cut: Record<string, unknown>): BevelCut | null {
  switch (cut.type) {
    case 'None':
      return null;
    case 'Single':
      if (cut.side !== 'Front' && cut.side !== 'Back')
        throw new ToolError('A Single bevel needs `side`: Front or Back.');
      return { cutType: 'Single', cutSide: cut.side, angle: requireAngle(cut, 'angle') };
    case 'Double':
      return {
        cutType: 'Double',
        frontAngle: requireAngle(cut, 'frontAngle'),
        backAngle: requireAngle(cut, 'backAngle'),
      };
    default:
      throw new ToolError('The bevel’s `type` must be None, Single, or Double.');
  }
}

export const memberCutTools = [
  defineTool<{ truss: string; member: string; against: string; at?: Point; remove?: boolean }>({
    name: 'cut_member',
    description:
      'Cuts one end of a member against another member’s face, as Cut Member (Shift) does: choose which of two ' +
      'meeting members runs through and which stops at the other, for example a web stopping at a chord, a chord ' +
      'stopping at an end vertical, or a slider’s end. The end nearest `against` is cut, at its nearest face; give ' +
      '`at` (a point near that end) when it’s ambiguous. `remove` takes off a cut against that member instead. ' +
      'get_truss lists what each member is cut against. Cuts are the member’s shape only; run Upgrade and Analyze ' +
      'afterward.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        member: { ...MEMBER_SCHEMA, description: `The member to cut. ${MEMBER_SCHEMA.description}` },
        against: { type: 'string', description: 'The member whose face it’s cut against, such as B1.' },
        at: {
          type: 'object',
          description: 'A point near the end to cut, in the truss’s coordinates.',
          properties: { x: { type: 'number' }, y: { type: 'number' } },
          required: ['x', 'y'],
        },
        remove: { type: 'boolean', description: 'Take the cut off instead. Defaults to false.' },
      },
      required: ['truss', 'member', 'against'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const opened = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const componentDesign = getActiveComponentDesign(getState()) ?? opened;
      const component = componentDesign.component;
      const member = findMemberByName(componentDesign, requireString(input, 'member'));
      const against = findMemberByName(componentDesign, requireString(input, 'against'));
      if (member.guid === against.guid) throw new ToolError('A member can’t be cut against itself.');
      if (!component.members.some((candidate) => candidate.guid === member.guid) || member.relativeGeometry == null)
        throw new ToolError(`${member.name} can’t be cut: only a truss’s own members can, not braces.`);

      const cuts = member.relativeGeometry;
      const cutGuids = cuts.geometryType === 'Member' ? [...cuts.baseCutGuids, ...cuts.endCutGuids] : [];
      const at = input.at;
      if (at != null && (typeof at !== 'object' || typeof at.x !== 'number' || typeof at.y !== 'number'))
        throw new ToolError('`at` must be a point, such as {"x": 48, "y": 3.5}.');
      const face = findCutFace(component, member, against, at);
      // Any of the other member's lines, its centerline included, counts as being cut against it.
      const againstLines = new Set(
        (component.referenceLines ?? [])
          .filter((line) =>
            line.lineType === 'Member'
              ? line.memberGeometryGuid === against.relativeGeometry?.guid
              : line.guid === against.relativeGeometry?.lineGuid,
          )
          .map((line) => line.guid),
      );
      const alreadyCut = (component.referenceLines ?? []).find(
        (line) => againstLines.has(line.guid) && cutGuids.includes(line.guid),
      );
      if (input.remove === true && alreadyCut == null)
        throw new ToolError(`${member.name} isn’t cut against ${against.name}.`);
      if (input.remove !== true && alreadyCut != null)
        throw new ToolError(
          `${member.name} is already cut against ${against.name}. To cut it at another of ${against.name}’s faces, ` +
            'take the cut off with `remove` first.',
        );

      // The app's handler takes a cut off when it's given a line the member is already cut by.
      const line = input.remove === true ? alreadyCut! : face.line;
      dispatch(handleReferenceLineClickDuringMemberCuttingMode(member, line, face.onLine as never));
      await waitForAppToSettle(getState);

      const edited = getState().componentDesigns.data[componentDesign.guid];
      if (edited == null) throw new ToolError('The truss is no longer in the project.');
      const after = edited.component.members.find((candidate) => candidate.guid === member.guid);
      if (after == null || JSON.stringify(after.relativeGeometry) === JSON.stringify(member.relativeGeometry))
        throw new ToolError(
          `The app made no change: ${input.remove === true ? 'taking off' : 'adding'} that cut wouldn’t change ` +
            `${member.name}’s shape.`,
        );
      return { summary: { cuts: { [after.name]: describeCuts(after, edited.component) }, ...summarizeTruss(edited) } };
    },
  }),

  defineTool<{ truss: string; members: string[]; end: 'Left' | 'Right' | 'Bottom'; bevel: Record<string, unknown> }>({
    name: 'set_bevel_cut',
    description:
      'Sets or removes a bevel cut on members’ ends or bottoms, as the member properties’ Bevel Cuts do, such as a ' +
      'bevel where a chord bears on a sloped wall.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        members: { type: 'array', items: MEMBER_SCHEMA },
        end: { type: 'string', enum: ['Left', 'Right', 'Bottom'] },
        bevel: BEVEL_SCHEMA,
      },
      required: ['truss', 'members', 'end', 'bevel'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const names = requireStringArray(input, 'members');
      if (names.length === 0) throw new ToolError('Name at least one member.');
      if (typeof input.bevel !== 'object' || input.bevel == null) throw new ToolError('Give the `bevel` to set.');
      const bevel = readBevelCut(input.bevel);
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));

      if (input.end === 'Bottom') {
        if (bevel?.cutType === 'Double') throw new ToolError('A bottom bevel can only be Single.');
        const bottom: BottomBevelCut | null = bevel == null ? null : { cutSide: bevel.cutSide, angle: bevel.angle };
        const active = getActiveComponentDesign(getState()) ?? componentDesign;
        await dispatch(
          setMemberBottomBevelCut(
            componentDesign.guid,
            names.map((name) => findMemberByName(active, name)),
            bottom,
          ),
        );
      } else if (input.end === 'Left' || input.end === 'Right') {
        // Each edit starts from the latest member, since the last one changed the truss.
        for (const name of names) {
          const active = getActiveComponentDesign(getState()) ?? componentDesign;
          await dispatch(setMemberBevelCut(componentDesign.guid, findMemberByName(active, name), input.end, bevel));
        }
      } else {
        throw new ToolError('`end` must be Left, Right, or Bottom.');
      }
      await waitForAppToSettle(getState);

      const edited = getState().componentDesigns.data[componentDesign.guid];
      if (edited == null) throw new ToolError('The truss is no longer in the project.');
      const key = input.end === 'Bottom' ? 'bottomBevelCut' : input.end === 'Left' ? 'leftBevelCut' : 'rightBevelCut';
      return {
        summary: {
          bevels: Object.fromEntries(
            names.map((name) => [name, (findMemberByName(edited, name) as unknown as Record<string, unknown>)[key]]),
          ),
        },
      };
    },
  }),
];
